// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import Action from 'core/fsmpack/statemachine/actions/Action';
import Vector3 from 'core/math/Vector3';

/**
 * @autodoc apply FsmAction
 */
class CopyJointTransformAction extends Action {

  constructor(options) {
    super(...arguments); // also support (id, settings) parameters used with FSM
    this.everyFrame = true;
    this.unitScaleConversion = new Vector3(1,1,1);
    this.unitScaleEntityTransformComponent = null;
  }

  enter(fsm) {
    // find unit scale conversion transform component on bound mesh entities to account for unit scale conversion
    const entity = fsm.getOwnerEntity();
    let parent = entity.transformComponent.parent;
    if (parent) {
      parent = parent.entity;
      if(parent.animationComponent && parent.animationComponent._skeletonPose && parent.transformComponent ) {
        this.unitScaleEntityTransformComponent = parent.transformComponent.children.find( childTransformComponent => {
          return childTransformComponent.entity.meshDataComponent && childTransformComponent.entity.meshDataComponent.currentPose === parent.animationComponent._skeletonPose;
        });
      }
    }
  }

  exit(fsm) {
    this.unitScaleEntityTransformComponent = null;
  }


  update(fsm) {
    if (this.jointIndex === null) { return; }

    const entity = fsm.getOwnerEntity();
    let parent = entity.transformComponent.parent;
    if (!parent) { return; }

    parent = parent.entity;
    if (!parent.animationComponent || !parent.animationComponent._skeletonPose) { return; }
    const pose = parent.animationComponent._skeletonPose;
    const jointTransform = pose._globalTransforms[this.jointIndex];
    if (!jointTransform) { return; }

    // account for unit scale conversion on bound mesh entities
    if(this.unitScaleEntityTransformComponent) {
      this.unitScaleConversion = this.unitScaleEntityTransformComponent.getWorldScale();
    }
    entity.transformComponent.transform.copy(jointTransform);
    entity.transformComponent.transform.translation.mul(this.unitScaleConversion);
    entity.transformComponent.setUpdated();
    entity.traverse(updateBounds);
  }
}

CopyJointTransformAction.external = {
  key: 'CopyJointTransform',
  deprecatedKey: 'Copy Joint Transform',
  name: 'Copy Joint Transform',
  type: 'animation',
  description: 'Copies a joint\'s transform from another entity, and applies it to this entity. This entity must be a child of an entity with an animation component.',
  parameters: [{
    name: 'Joint',
    key: 'jointIndex',
    type: 'int',
    control: 'jointSelector',
    default: null,
    description: 'Joint transform to copy.',
  }],
  transitions: [],
  autoStop: false
};

function updateBounds(entity) {
  if (entity && entity.meshDataComponent && entity.meshRendererComponent) {
    entity.meshRendererComponent.updateBounds(
      entity.meshDataComponent.modelBound,
      entity.transformComponent.sync().worldTransform,
    );
  }
}

CopyJointTransformAction.register();

export default CopyJointTransformAction;
